import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Globe,
  CheckCircle,
  Search,
  MapPin,
  User,
  Loader2
} from 'lucide-react';

const Integrantes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('Todos');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar datos desde JSON
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`/data/integrantes.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Error al cargar datos');
        const jsonData = await response.json();
        setData(jsonData);
      } catch (error) {
        console.error('Error cargando integrantes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Error al cargar los datos</p>
      </div>
    );
  }

  const integrantes = data.integrantes || [];
  const { titulo, subtitulo, membresia } = data;

  // Obtener lista única de países
  const paises = ['Todos', ...Array.from(new Set(integrantes.map(i => i.pais))).sort()];

  // Filtrar integrantes
  const integrantesFiltrados = integrantes.filter(integrante => {
    const matchesSearch = integrante.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = selectedCountry === 'Todos' || integrante.pais === selectedCountry;
    return matchesSearch && matchesCountry;
  });

  // Agrupar por país
  const integrantesPorPais = integrantesFiltrados.reduce((acc, integrante) => {
    if (!acc[integrante.pais]) {
      acc[integrante.pais] = [];
    }
    acc[integrante.pais].push(integrante);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Users className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {titulo}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-2">
                {subtitulo}
              </p>
              <p className="text-lg text-white/80">
                {integrantes.length} miembros de {paises.length - 1} países
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Información de membresía */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg shadow-xl p-8">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-3">{membresia.titulo}</h2>
                <p className="text-lg text-white/90">
                  {membresia.descripcion}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Búsqueda por nombre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Buscar por nombre
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar integrante..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none"
                  />
                </div>
              </div>

              {/* Filtro por país */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtrar por país
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none appearance-none bg-white"
                  >
                    {paises.map((pais) => (
                      <option key={pais} value={pais}>
                        {pais}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contador de resultados */}
            <div className="mt-4 text-sm text-gray-600">
              Mostrando {integrantesFiltrados.length} de {integrantes.length} integrantes
            </div>
          </div>
        </section>

        {/* Lista de integrantes por país */}
        <section>
          {Object.keys(integrantesPorPais).sort().map((pais) => (
            <div key={pais} className="mb-8">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
                <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white p-6">
                  <h2 className="text-2xl font-bold flex items-center gap-3">
                    <MapPin className="w-6 h-6" />
                    {pais}
                    <span className="text-lg font-normal text-white/80">
                      ({integrantesPorPais[pais].length} {integrantesPorPais[pais].length === 1 ? 'integrante' : 'integrantes'})
                    </span>
                  </h2>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {integrantesPorPais[pais].sort((a, b) => a.nombre.localeCompare(b.nombre)).map((integrante, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 bg-gradient-to-r from-gray-50 to-white p-4 rounded-lg border-2 border-gray-200 hover:border-[#0b742f] hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex-shrink-0">
                          <div className="bg-[#0b742f]/10 p-2 rounded-full">
                            <User className="w-5 h-5 text-[#0b742f]" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium truncate">
                            {integrante.nombre}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Mensaje si no hay resultados */}
          {integrantesFiltrados.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                No se encontraron integrantes
              </h3>
              <p className="text-gray-600">
                Intenta ajustar los filtros de búsqueda
              </p>
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default Integrantes;
